import { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, FileText, X, Star, Eye, EyeOff, Download, Loader2 } from 'lucide-react';
import { api, fetchFileBlobUrl, fileToUpload, ApiError } from '@/lib/api';
import { fileSize, humanise } from '@/lib/format';
import { Button, Modal, cn, useToast, EmptyState, Badge } from './ui';
import type { StoredFile, FileKind } from '@shared/types';

/**
 * Images are served from PROVIA Storage through our authenticated endpoint, which
 * <img src> cannot reach on its own — the auth header has to be attached. So
 * each image is fetched as a blob and rendered from an object URL.
 */
export function DriveImage({ fileId, alt, className, fallback }: {
  fileId: string | null | undefined;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!fileId) return;
    let revoked = false;
    let objectUrl: string | null = null;

    fetchFileBlobUrl(fileId)
      .then((u) => {
        if (revoked) { URL.revokeObjectURL(u); return; }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setFailed(true));

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);

  if (!fileId || failed) {
    return (
      <div className={cn('flex items-center justify-center bg-raised text-faint', className)}>
        {fallback ?? <ImageIcon className="h-6 w-6" />}
      </div>
    );
  }

  if (!url) {
    return <div className={cn('skeleton', className)} />;
  }

  return <img src={url} alt={alt} className={className} loading="lazy" />;
}

/** Drag-and-drop / click uploader. Images are downscaled client-side first. */
export function FileUploader({ onUpload, kind, accept, label, multiple = true, disabled, disabledReason }: {
  onUpload: (payload: { filename: string; mime_type: string; data: string }) => Promise<void>;
  kind?: FileKind;
  accept?: string;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const toast = useToast();

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;
    const list = Array.from(files).slice(0, multiple ? 20 : 1);

    setBusy(true);
    setProgress({ done: 0, total: list.length });
    let failures = 0;

    for (let i = 0; i < list.length; i++) {
      try {
        const payload = await fileToUpload(list[i]);
        await onUpload(payload);
      } catch (err) {
        failures++;
        toast.error(
          err instanceof ApiError
            ? `${list[i].name}: ${err.message}`
            : `Could not upload ${list[i].name}`,
        );
      }
      setProgress({ done: i + 1, total: list.length });
    }

    setBusy(false);
    setProgress({ done: 0, total: 0 });
    if (failures === 0) {
      toast.success(list.length === 1 ? 'File uploaded' : `${list.length} files uploaded`);
    }
    if (inputRef.current) inputRef.current.value = '';
  }, [onUpload, multiple, disabled, toast]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        void handleFiles(e.dataTransfer.files);
      }}
      onClick={() => !disabled && !busy && inputRef.current?.click()}
      className={cn(
        'border-2 border-dashed rounded-2xl p-6 text-center transition-colors',
        disabled ? 'border-line opacity-60 cursor-not-allowed'
          : 'cursor-pointer hover:border-brand/50 hover:bg-raised/40',
        dragging ? 'border-brand bg-brand/5' : 'border-line',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept ?? (kind?.includes('photo') ? 'image/*' : undefined)}
        multiple={multiple}
        onChange={(e) => void handleFiles(e.target.files)}
        disabled={disabled}
      />

      {busy ? (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
          <p className="text-sm text-muted">
            Uploading {progress.done} of {progress.total}…
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-6 w-6 text-faint" />
          <p className="text-sm font-medium text-ink">
            {label ?? 'Drop files here, or click to choose'}
          </p>
          <p className="text-xs text-faint">
            {disabled
              ? disabledReason ?? 'Uploads are unavailable'
              : 'Images are resized automatically · 25 MB limit'}
          </p>
        </div>
      )}
    </div>
  );
}

/** Grid of photos with cover / visibility / delete controls. */
export function PhotoGrid({ files, onChange, readOnly, emptyMessage }: {
  files: StoredFile[];
  onChange?: () => void;
  readOnly?: boolean;
  emptyMessage?: string;
}) {
  const [preview, setPreview] = useState<StoredFile | null>(null);
  const toast = useToast();

  if (files.length === 0) {
    return (
      <EmptyState
        icon={<ImageIcon className="h-6 w-6" />}
        title="No photos yet"
        message={emptyMessage ?? 'Upload photos to show them here and in the tenant portal.'}
      />
    );
  }

  async function setCover(file: StoredFile) {
    try {
      await api.post(`/files/${file.id}/set-cover`);
      toast.success('Cover photo updated');
      onChange?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not set the cover');
    }
  }

  async function toggleVisible(file: StoredFile) {
    try {
      await api.patch(`/files/${file.id}`, {
        caption: file.caption,
        sort_order: file.sort_order,
        tenant_visible: !file.tenant_visible,
      });
      toast.success(file.tenant_visible ? 'Hidden from tenants' : 'Visible to tenants');
      onChange?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update visibility');
    }
  }

  async function remove(file: StoredFile) {
    try {
      await api.del(`/files/${file.id}`);
      toast.success('Photo removed');
      onChange?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove the photo');
    }
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {files.map((file) => (
          <div key={file.id} className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-line bg-raised">
            <button className="w-full h-full" onClick={() => setPreview(file)}>
              <DriveImage
                fileId={file.id}
                alt={file.caption ?? file.name}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
            </button>

            {!file.tenant_visible && (
              <div className="absolute top-2 left-2">
                <Badge tone="neutral" className="bg-black/60 backdrop-blur border-transparent text-white">
                  <EyeOff className="h-3 w-3" /> Hidden
                </Badge>
              </div>
            )}

            {!readOnly && (
              <div className="absolute inset-x-0 bottom-0 p-2 flex items-center gap-1
                              bg-gradient-to-t from-black/80 to-transparent
                              opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => void setCover(file)}
                  className="p-1.5 rounded-lg bg-black/50 text-white hover:bg-black/70"
                  title="Make cover photo"
                >
                  <Star className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => void toggleVisible(file)}
                  className="p-1.5 rounded-lg bg-black/50 text-white hover:bg-black/70"
                  title={file.tenant_visible ? 'Hide from tenants' : 'Show to tenants'}
                >
                  {file.tenant_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                </button>
                <button
                  onClick={() => void remove(file)}
                  className="p-1.5 rounded-lg bg-black/50 text-white hover:bg-danger ml-auto"
                  title="Delete"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal
        open={!!preview}
        onClose={() => setPreview(null)}
        title={preview?.caption ?? preview?.name ?? ''}
        subtitle={preview ? `${humanise(preview.kind)} · ${fileSize(preview.size_bytes)}` : undefined}
        size="lg"
      >
        {preview && (
          <DriveImage
            fileId={preview.id}
            alt={preview.caption ?? preview.name}
            className="w-full rounded-xl object-contain max-h-[70vh] bg-raised"
          />
        )}
      </Modal>
    </>
  );
}

/** List view for non-image documents. */
export function DocumentList({ files, onChange, readOnly, onShare }: {
  files: StoredFile[];
  onChange?: () => void;
  readOnly?: boolean;
  onShare?: (file: StoredFile) => void;
}) {
  const [viewing, setViewing] = useState<StoredFile | null>(null);
  const toast = useToast();

  if (files.length === 0) {
    return <EmptyState icon={<FileText className="h-6 w-6" />} title="No documents yet" />;
  }

  async function remove(file: StoredFile) {
    try {
      await api.del(`/files/${file.id}`);
      toast.success('Document removed');
      onChange?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove it');
    }
  }

  return (
    <>
      <div className="space-y-2">
        {files.map((file) => (
          <div
            key={file.id}
            className="flex items-center gap-3 p-3 rounded-xl border border-line bg-raised/40 row-hover"
          >
            <div className="h-9 w-9 rounded-lg bg-surface border border-line flex items-center
                            justify-center shrink-0 text-muted">
              {file.mime_type.startsWith('image/')
                ? <ImageIcon className="h-4 w-4" />
                : <FileText className="h-4 w-4" />}
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{file.caption || file.name}</p>
              <p className="text-xs text-faint">
                {humanise(file.kind)} · {fileSize(file.size_bytes)}
                {file.tenant_visible ? ' · Visible to tenant' : ''}
              </p>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => setViewing(file)} title="View">
                <Eye className="h-4 w-4" />
              </Button>
              {onShare && (
                <Button size="icon" variant="ghost" onClick={() => onShare(file)} title="Create share link">
                  <Download className="h-4 w-4" />
                </Button>
              )}
              {!readOnly && (
                <Button size="icon" variant="ghost" onClick={() => void remove(file)} title="Delete">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing?.name ?? ''}
        subtitle={viewing ? `${humanise(viewing.kind)} · ${fileSize(viewing.size_bytes)}` : undefined}
        size="xl"
      >
        {viewing && <FileViewer file={viewing} />}
      </Modal>
    </>
  );
}

/** Renders images inline and PDFs in an iframe; anything else offers a download. */
function FileViewer({ file }: { file: StoredFile }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    fetchFileBlobUrl(file.id)
      .then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setError(true));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id]);

  if (error) {
    return <p className="text-sm text-danger">That file could not be loaded from PROVIA Storage.</p>;
  }
  if (!url) {
    return <div className="skeleton h-96 w-full" />;
  }

  if (file.mime_type.startsWith('image/')) {
    return <img src={url} alt={file.name} className="w-full rounded-xl object-contain max-h-[70vh] bg-raised" />;
  }
  if (file.mime_type === 'application/pdf') {
    return <iframe src={url} title={file.name} className="w-full h-[70vh] rounded-xl border border-line bg-white" />;
  }

  return (
    <div className="text-center py-10">
      <FileText className="h-10 w-10 text-faint mx-auto mb-3" />
      <p className="text-sm text-muted mb-4">This file type cannot be previewed in the browser.</p>
      <a href={url} download={file.name}>
        <Button variant="primary" icon={<Download className="h-4 w-4" />}>Download</Button>
      </a>
    </div>
  );
}
